// Small dependency-free Markdown renderer used by the T&C surfaces. Handles
// the subset legal documents actually use: headings, paragraphs, ul/ol,
// bold/italic/code, hr, blockquotes, and pipe tables. Classes are picked to
// read well against the app's theme tokens (text-text-dark-*, bg-primary,
// border-gray-200 dark:border-gray-800).
export function renderTermsMarkdown(src) {
    if (!src) return '';
    const escape = (s)=>s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    // Only these protocols may appear in a rendered link. Terms content is
    // admin-authored, but it is stored in the DB and rendered on the public
    // landing page, so a compromised back-office account must not be able to
    // turn `[x](javascript:...)` into script execution.
    const safeHref = (href)=>{
        const trimmed = href.trim();
        return /^(https?:|mailto:|tel:|#|\/)/i.test(trimmed) ? trimmed : '#';
    };
    const inline = (s)=>s.replace(/`([^`]+)`/g, '<code class="rounded bg-gray-100 dark:bg-gray-800 px-1 py-0.5 text-[0.85em] font-mono">$1</code>').replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold">$1</strong>').replace(/\*([^*]+)\*/g, '<em>$1</em>').replace(/_([^_]+)_/g, '<em>$1</em>').replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, text, href)=>`<a href="${safeHref(href)}" target="_blank" rel="noreferrer" class="text-primary underline-offset-2 hover:underline">${text}</a>`);
    const HEADING_CLASSES = {
        1: 'mt-4 mb-2 text-xl font-semibold text-gray-900 dark:text-white',
        2: 'mt-4 mb-2 text-lg font-semibold text-gray-900 dark:text-white',
        3: 'mt-3 mb-1.5 text-base font-semibold text-gray-900 dark:text-white',
        4: 'mt-3 mb-1 text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide',
        5: 'mt-2 mb-1 text-sm font-medium text-gray-900 dark:text-white',
        6: 'mt-2 mb-1 text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider'
    };
    const out = [];
    let inUl = false;
    let inOl = false;
    let inTable = false;
    let inQuote = false;
    let para = [];
    const flushPara = ()=>{
        if (para.length) {
            out.push(`<p class="mb-3 text-gray-700 dark:text-gray-300 leading-relaxed">${inline(escape(para.join(' ')))}</p>`);
            para = [];
        }
    };
    const closeLists = ()=>{
        if (inUl) {
            out.push('</ul>');
            inUl = false;
        }
        if (inOl) {
            out.push('</ol>');
            inOl = false;
        }
        if (inTable) {
            out.push('</tbody></table></div>');
            inTable = false;
        }
        if (inQuote) {
            out.push('</blockquote>');
            inQuote = false;
        }
    };
    for (const raw of src.split(/\r?\n/)){
        const line = raw.trimEnd();
        if (!line.trim()) {
            flushPara();
            closeLists();
            continue;
        }
        const h = /^(#{1,6})\s+(.*)$/.exec(line);
        if (h) {
            flushPara();
            closeLists();
            const level = h[1].length;
            out.push(`<h${level} class="${HEADING_CLASSES[level]}">${inline(escape(h[2]))}</h${level}>`);
            continue;
        }
        if (/^---+$/.test(line)) {
            flushPara();
            closeLists();
            out.push('<hr class="my-4 border-gray-200 dark:border-gray-800" />');
            continue;
        }
        const quote = /^>\s+(.*)$/.exec(line);
        if (quote) {
            flushPara();
            if (inUl || inOl || inTable) closeLists();
            if (!inQuote) {
                out.push('<blockquote class="my-3 border-l-2 border-primary bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 italic">');
                inQuote = true;
            }
            out.push(`<p class="mb-1 last:mb-0">${inline(escape(quote[1]))}</p>`);
            continue;
        }
        const li = /^[-*]\s+(.*)$/.exec(line);
        if (li) {
            flushPara();
            if (inOl || inTable || inQuote) closeLists();
            if (!inUl) {
                out.push('<ul class="mb-3 list-disc pl-5 space-y-1 text-gray-700 dark:text-gray-300 marker:text-primary">');
                inUl = true;
            }
            out.push(`<li>${inline(escape(li[1]))}</li>`);
            continue;
        }
        const oli = /^\d+\.\s+(.*)$/.exec(line);
        if (oli) {
            flushPara();
            if (inUl || inTable || inQuote) closeLists();
            if (!inOl) {
                out.push('<ol class="mb-3 list-decimal pl-5 space-y-1 text-gray-700 dark:text-gray-300 marker:text-primary marker:font-semibold">');
                inOl = true;
            }
            out.push(`<li>${inline(escape(oli[1]))}</li>`);
            continue;
        }
        if (/^\|.*\|$/.test(line)) {
            flushPara();
            if (inUl || inOl || inQuote) closeLists();
            const cells = line.slice(1, -1).split('|').map((c)=>c.trim());
            if (!inTable) {
                out.push('<div class="mb-3 overflow-x-auto rounded-md border border-gray-200 dark:border-gray-800"><table class="w-full text-left text-sm"><thead class="bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white"><tr>' + cells.map((c)=>`<th class="px-3 py-2 font-medium border-b border-gray-200 dark:border-gray-800">${inline(escape(c))}</th>`).join('') + '</tr></thead><tbody class="text-gray-700 dark:text-gray-300">');
                inTable = true;
            } else if (/^\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?$/.test(line)) {
            // separator
            } else {
                out.push('<tr class="border-b border-gray-200 dark:border-gray-800/60 last:border-b-0">' + cells.map((c)=>`<td class="px-3 py-2">${inline(escape(c))}</td>`).join('') + '</tr>');
            }
            continue;
        }
        para.push(line.trim());
    }
    flushPara();
    closeLists();
    return out.join('\n');
}

//# sourceMappingURL=markdown.js.map